import {useNavigate, useSearchParams} from 'react-router-dom';
import {ChevronLeft} from 'lucide-react';
import {cn} from '@/matic/lib/utils/cn';
import {
  RELAY_CONFIG_PATH,
  RELAY_CONFIG_SECTIONS,
  resolveRelayConfigSection,
} from '@/matic/features/relay/components/config/relayConfigSections';

/**
 * Replaces the normal sidebar nav body while the user is on the relay
 * configuration surface. Selecting a section swaps the right-hand content
 * (via the ``?section=`` query param) without leaving the page; the Back
 * row pops the single history entry that entered config mode, restoring
 * whatever nav the user came from.
 */
export default function RelayConfigSidebarNav() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeSection = resolveRelayConfigSection(searchParams.get('section'));

  return (
    <nav className="flex-1 overflow-y-auto py-4 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-sidebar-active/50 hover:[&::-webkit-scrollbar-thumb]:bg-sidebar-active [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5">
      <button
        type="button"
        onClick={() => {
          navigate(-1);
        }}
        className="flex w-full items-center px-6 py-2 text-left text-sidebar-fg/70 transition-colors hover:bg-sidebar-hover hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
      >
        <ChevronLeft className="mr-3 h-4 w-4 shrink-0" />
        <span className="text-sm font-medium">Back</span>
      </button>

      <div className="mb-2 mt-4 px-6 text-xs font-semibold uppercase tracking-wider text-sidebar-fg/50">
        Settings
      </div>

      <div className="flex flex-col gap-y-1">
        {RELAY_CONFIG_SECTIONS.map((section) => {
          const Icon = section.icon;
          const isActive = section.id === activeSection;
          return (
            <button
              key={section.id}
              type="button"
              onClick={() => {
                navigate(`${RELAY_CONFIG_PATH}?section=${section.id}`, {
                  replace: true,
                });
              }}
              className={cn(
                'relative flex w-full items-center py-2 pl-6 pr-4 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
                isActive
                  ? 'text-primary-500'
                  : 'text-sidebar-fg hover:bg-sidebar-hover hover:text-white'
              )}
            >
              {isActive && (
                <div className="absolute bottom-0 left-0 top-0 w-1 bg-primary-500" />
              )}
              <Icon
                className={cn(
                  'mr-3 h-4 w-4 shrink-0',
                  isActive && 'text-primary-500'
                )}
              />
              <span className="text-sm font-medium">{section.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}
